import { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { BorderButton, StyledButton } from '@elements/Button';
import {
  ControlledPhoneFormField,
  ControlledTextFormField,
  CountryDropdownFormField,
  DropdownFormField,
} from '@elements/FormField';
import { LoaderBar } from '@components/LoaderBar';
import {
  ErrorMessageConstant,
  ShippingSpeeds,
} from '@utility/constants/common';
import OrdersService from '@utility/services/order';
import { useForm } from 'react-hook-form';
import ShippingLabelService from '@utility/services/shipping-label';
import { marketplaceRegionCountryValueHook } from '@hooks/marketplace-region';
import { orderAddressSuggestionHook } from '@hooks/order';
import { MuteText, StyledToggle } from '@elements/Common';
import { AppModal } from '@elements/Modal';
import { Alert, Button } from '@skupreme/shared/ui';
import { ToastMessage } from '@utility/toast';
import { DarkPopup } from '@elements/Popup';
import { addressValidationText } from '@utility/constants/strings';
import { Info } from '@phosphor-icons/react';
import { dropdownOptionsIncludeFlag } from '@elements/Flag';

/* Semantic's equal-width group: side by side, stacked on a phone. */
const FIELD_PAIR = 'sk:grid sk:grid-cols-1 sk:gap-x-3 sk:sm:grid-cols-2';

const ADDRESS_LINE_MAX_LENGTH = 35;

const addressSchema = yup.object().shape({
  address_one: yup.string().trim().required(),
  address_two: yup
    .string()
    .trim()
    .max(
      ADDRESS_LINE_MAX_LENGTH,
      `Address Line 2 must not exceed ${ADDRESS_LINE_MAX_LENGTH} characters`,
    )
    .nullable(),
  city: yup.string().trim().required(),
  state: yup
    .string()
    .trim()
    .when('country', (country, schema) => {
      if (country?.toLowerCase?.() === 'us') {
        return schema.required();
      }
      return schema.optional();
    }),
  country: yup.string().trim().required(),
  postal_code: yup.string().trim().required(),
  name: yup.string().trim().nullable(),
  company: yup.string().trim().nullable(),
  email: yup.string().trim().email(),
  phone: yup.string().trim().nullable(),
  shipping_speed: yup.string().nullable(),
});

const speedOptions = [
  {
    text: ShippingSpeeds.Standard,
    value: ShippingSpeeds.Standard,
    key: ShippingSpeeds.Standard,
  },
  {
    text: ShippingSpeeds.Expedited,
    value: ShippingSpeeds.Expedited,
    key: ShippingSpeeds.Expedited,
  },
  {
    text: ShippingSpeeds.Priority,
    value: ShippingSpeeds.Priority,
    key: ShippingSpeeds.Priority,
  },
];

export function AddressValidationPopup({
  open,
  closeModal,
  currentAddressData,
  orderId,
  setFormAddressOverride,
  isValidAddress = true,
  addressValidationData = {},
  addressValidationError,
  notManual,
  isManualUpdate = false,
  isNotShipped = true,
  /* EN-4544: a V2 order saves its ship-to here: the refusal to show, or null once saved. */
  saveShipTo = null as
    | ((address: Record<string, unknown>) => Promise<string | null>)
    | null,
}) {
  const { data: marketplaceData, loading: marketplaceLoading } =
    marketplaceRegionCountryValueHook();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(addressSchema),
    shouldUnregister: true,
  });
  const [saving, setSaving] = useState(false);
  const [override, setOverride] = useState(false);
  const [countryCode, setCountryCode] = useState();
  const [isPhoneAvailable, setIsPhoneAvailable] = useState(false);
  const [error, setError] = useState('');

  const watchFields = watch();

  const [showSuggestion, setShowSuggestion] = useState(true);
  const { data: suggestedAddress } = orderAddressSuggestionHook(
    watchFields?.address_one ? watchFields : currentAddressData,
    isNotShipped && !saveShipTo,
  );

  const isUS = useMemo(
    () => watchFields.country?.toLowerCase?.() === 'us',
    [watchFields.country],
  );

  useEffect(() => {
    if (open && currentAddressData && marketplaceData.length > 0) {
      const country = marketplaceData.find(
        // @ts-expect-error - TS2339: Property 'name' does not exist on type 'never'.
        x => x.name === currentAddressData.country,
      );
      reset({
        ...(currentAddressData || {}),
        phone: currentAddressData?.phone?.toString() || '',
        // @ts-expect-error - TS2339: Property 'value' does not exist on type 'never'.
        country: country?.value,
      });
      setError('');
    }
  }, [open, marketplaceData]);

  useEffect(() => {
    setOverride(currentAddressData?.override);
    setIsPhoneAvailable(!!currentAddressData?.phone);
  }, [currentAddressData]);

  const onSubmit = async data => {
    try {
      setSaving(true);
      setError('');
      if (saveShipTo) {
        const refusal = await saveShipTo(data);
        if (refusal) {
          setError(refusal);
          return;
        }
        await close({
          address_line_one: data.address_one,
          company: data.company,
          address_line_two: data.address_two,
          city: data.city,
          zip: data.postal_code,
          state: data.state,
          country: data.country,
          name: data.name,
          e_mail: data.email,
          phone: data.phone || '',
        });
        return;
      }
      const validationReqData = {
        order_id: orderId,
        override,
        autoSubmit: true,
        ...data,
      };
      delete validationReqData.phone;
      delete validationReqData.name;
      delete validationReqData.email;
      delete validationReqData.shipping_speed;
      delete validationReqData.company;
      const result =
        await ShippingLabelService.addressValidation(validationReqData);

      const validation = result.data;
      // The backend fails OPEN on provider-side problems (quota, timeout, auth,
      // missing credentials) and fails CLOSED only on a genuinely undeliverable
      // address. In the form we mirror that: block the save for invalid_address
      // and let provider_unavailable through with a soft warning.
      const providerUnavailable =
        !!validation &&
        validation.is_valid === false &&
        validation.error_type === 'provider_unavailable';

      if (!validation || (!validation.is_valid && !providerUnavailable)) {
        setError(validation?.error || validation?.description);
      } else {
        if (providerUnavailable) {
          ToastMessage.warning(
            validation.error ||
              'We could not verify this address right now, so it was saved as entered.',
          );
        }
        const reqData = {
          order_id: orderId,
          address_line_one: result.data.address_one,
          address_line_two: result.data.address_two,
          city: result.data.city,
          postal_code: result.data.postal_one || result.data.postal_code,
          state: result.data.state,
          // @ts-expect-error - TS2532: Object is possibly 'undefined'.
          country: marketplaceData.find(x => x.value === data.country).id,
          name: data.name,
          email: data.email,
          phone: data.phone || '',
          override,
          shipping_speed: data.shipping_speed,
          company: data.company,
        };

        if (!reqData.shipping_speed) {
          delete reqData.shipping_speed;
        }

        if (result.data.postal_two) {
          reqData.postal_code += `-${result.data.postal_two}`;
        }

        await OrdersService.updateAddress(reqData);
        await close({
          address_line_one: reqData.address_line_one,
          company: reqData.company,
          address_line_two: reqData.address_line_two,
          city: reqData.city,
          zip: reqData.postal_code,
          state: reqData.state,
          country: data.country,
          name: data.name,
          e_mail: data.email,
          override,
          phone: data.phone || '',
          shipping_speed: data.shipping_speed,
        });
      }
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setSaving(false);
    }
  };

  const close = async id => {
    reset({});
    await closeModal(id);
  };

  useEffect(() => {
    setShowSuggestion(!!suggestedAddress?.formatted_address);
  }, [suggestedAddress?.formatted_address]);

  const useAddress = () => {
    const country = marketplaceData.find(
      // @ts-expect-error - TS2339: Property 'name' does not exist on type 'never'.
      x => x.name === suggestedAddress.country,
    );
    reset({
      ...suggestedAddress,
      name:
        suggestedAddress?.name?.toString() ??
        currentAddressData?.name?.toString() ??
        '',
      email:
        suggestedAddress?.email?.toString() ??
        currentAddressData?.email?.toString() ??
        '',
      phone:
        suggestedAddress?.phone?.toString() ??
        currentAddressData?.phone?.toString() ??
        '',
      postal_code: suggestedAddress.postal_one?.toString(),
      // @ts-expect-error - TS2339: Property 'value' does not exist on type 'never'.
      country: country?.value?.toString(),
    });
    discard();
  };

  const discard = () => {
    setShowSuggestion(false);
  };

  const onCurrencyChange = item => {
    // @ts-expect-error - TS2345: Argument of type '"phone"' is not assignable to parameter of type 'never'.
    setValue('phone', null);
    if (!isPhoneAvailable) {
      const selectedCountryCode = marketplaceData.find(
        // @ts-expect-error - TS2339: Property 'id' does not exist on type 'never'.
        cd => cd.id === item.value,
        // @ts-expect-error - TS2339: Property 'flag' does not exist on type 'never'.
      )?.flag;
      setCountryCode(selectedCountryCode);
    }
  };

  return (
    <AppModal
      open={open}
      title={isManualUpdate ? 'Update Address' : 'Address Validation'}
      closeModal={closeModal}
    >
      {saving && <LoaderBar content="Saving" />}
      {override ? (
        <Alert tone="danger" className="sk:my-2">
          The address you entered couldn&apos;t be located in our system. Are
          you certain you want to proceed with shipping to this address?
        </Alert>
      ) : !suggestedAddress?.is_valid && error ? (
        <Alert tone="danger" className="sk:my-2">
          {error}
          {saveShipTo ? null : (
            <div className="sk:mt-1">
              Enable <b>Manual Override</b> below to ship to this address as
              entered.
            </div>
          )}
        </Alert>
      ) : (
        !isValidAddress && (
          <Alert tone="danger" className="sk:my-2">
            {
              // @ts-expect-error - ts-migration
              addressValidationData?.error || addressValidationError
                ? // @ts-expect-error - TS2339: Property 'error' does not exist on type '{}'.
                  addressValidationData?.error || addressValidationError
                : 'Invalid Address'
            }
          </Alert>
        )
      )}

      <form
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        className="sk:mt-3"
      >
        <ControlledTextFormField
          error={errors.address_one}
          label="Address Line 1"
          name="address_one"
          control={control}
          required
        />
        <ControlledTextFormField
          error={errors.address_two}
          hint={errors.address_two?.message}
          name="address_two"
          control={control}
          maxLength={ADDRESS_LINE_MAX_LENGTH}
          showCharacterCount
          placeholder="Address Line 2"
          label={
            <div className="sk:mb-1 sk:flex sk:items-center sk:gap-2">
              <MuteText>Address Line 2</MuteText>
              <DarkPopup
                position="top center"
                trigger={<Info className="sk:text-fg-tertiary" />}
              >
                Avoid using special characters — the carrier does not support
                them. They’ll be automatically removed. Use standard
                abbreviations like &quot;STE&quot; or &quot;UNIT&quot; instead.
              </DarkPopup>
            </div>
          }
        />
        <div className={FIELD_PAIR}>
          <ControlledTextFormField
            error={errors.city}
            label="City"
            name="city"
            control={control}
            required
          />
          <ControlledTextFormField
            error={errors.state}
            label={
              <div className="sk:inline-flex sk:items-center sk:gap-1">
                State (Abbreviated){' '}
                <DarkPopup trigger={<Info />} hoverable position="top center">
                  {addressValidationText.stateInfo}
                </DarkPopup>
              </div>
            }
            name="state"
            control={control}
            required={isUS}
          />
        </div>
        <div className={FIELD_PAIR}>
          <ControlledTextFormField
            error={errors.postal_code}
            label="Zip Code"
            name="postal_code"
            control={control}
            required
          />
          {/* @ts-expect-error - TS2739: Type '{ options: { image: { src: string; }; flag: null; key?... */}
          <CountryDropdownFormField
            options={dropdownOptionsIncludeFlag(marketplaceData)}
            loading={marketplaceLoading}
            name="country"
            error={errors.country}
            label="Country"
            control={control}
            defaultValue=""
            dropdownChanged={onCurrencyChange}
            required
          />
        </div>
        <div className={FIELD_PAIR}>
          <ControlledTextFormField
            error={errors.name}
            label="Customer Name"
            name="name"
            control={control}
          />
          <ControlledTextFormField
            error={errors.email}
            label="Email Address"
            name="email"
            control={control}
          />
        </div>
        <div className={FIELD_PAIR}>
          <ControlledPhoneFormField
            error={errors.phone}
            label="Mobile Number"
            name="phone"
            control={control}
            countryCode={countryCode}
            country={(watchFields?.country || 'us').toLowerCase()}
            disableCountryGuess
          />
          <ControlledTextFormField
            error={errors.company}
            label="Company Name"
            name="company"
            control={control}
          />
        </div>
        {notManual && !saveShipTo && (
          <DropdownFormField
            error={errors.name}
            label="Shipping Speed"
            name="shipping_speed"
            control={control}
            options={speedOptions}
          />
        )}
        <div className={FIELD_PAIR}>
          <div className="sk:mt-2">
            {/* A V2 ship-to has no override: what the API refuses is said above. */}
            {saveShipTo ? null : (
              <StyledToggle
                label="Manual Override"
                defaultChecked={override}
                className="danger"
                onChange={(_, data) => {
                  setOverride(data.checked);
                  setFormAddressOverride?.(data.checked);
                }}
              />
            )}
          </div>
          <div className="sk:mb-2 sk:flex sk:items-center sk:justify-end sk:gap-1">
            <BorderButton type="button" onClick={() => closeModal()}>
              Cancel
            </BorderButton>
            <StyledButton type="submit">Save</StyledButton>
          </div>
        </div>
      </form>
      {showSuggestion && (
        <Alert
          tone="success"
          className="sk:mt-3"
          title="Suggested Address"
          action={
            <div className="sk:flex sk:gap-2">
              <Button size="sm" onPress={discard}>
                Discard
              </Button>
              <Button variant="primary" size="sm" onPress={useAddress}>
                Use
              </Button>
            </div>
          }
        >
          {suggestedAddress?.formatted_address}
        </Alert>
      )}
    </AppModal>
  );
}
